import { useState, useEffect } from 'react';
import { Shield, AlertCircle, Check, Clock, X } from 'lucide-react';
import { motion } from 'framer-motion';

const STATUS_CONFIG = {
  NOT_STARTED: {
    label: 'Complete KYC',
    color: 'text-yellow-400 bg-yellow-400/10 border-yellow-400/30',
    icon: AlertCircle,
    tooltip: 'Complete your KYC to start investing'
  },
  IN_PROGRESS: {
    label: 'KYC Incomplete',
    color: 'text-orange-400 bg-orange-400/10 border-orange-400/30',
    icon: Clock,
    tooltip: 'Complete all KYC steps to submit'
  },
  SUBMITTED: {
    label: 'KYC Pending',
    color: 'text-blue-400 bg-blue-400/10 border-blue-400/30',
    icon: Clock,
    tooltip: 'Your KYC is being verified'
  },
  VERIFIED: {
    label: 'KYC Verified',
    color: 'text-[#4ADE80] bg-[#4ADE80]/10 border-[#4ADE80]/30',
    icon: Check,
    tooltip: 'You can invest now!'
  },
  REJECTED: {
    label: 'KYC Rejected',
    color: 'text-red-400 bg-red-400/10 border-red-400/30',
    icon: X,
    tooltip: 'Please review and resubmit your KYC'
  }
};

export default function KYCStatusBadge({ 
  userId, 
  onClick, 
  showTooltip = true,
  size = 'default' // 'small' | 'default' | 'large'
}) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showHover, setShowHover] = useState(false);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    if (userId) {
      fetchStatus();
    }
  }, [userId]);

  const fetchStatus = async () => {
    try {
      const response = await fetch(`${API_URL}/api/investor/kyc/status/${userId}`);
      const data = await response.json();
      setStatus(data.status || 'NOT_STARTED');
    } catch (error) {
      setStatus('NOT_STARTED');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="animate-pulse bg-white/10 rounded-full h-6 w-24" />
    );
  }

  const config = STATUS_CONFIG[status] || STATUS_CONFIG.NOT_STARTED;
  const Icon = config.icon;
  
  const sizeClasses = {
    small: 'text-xs px-2 py-0.5',
    default: 'text-xs px-3 py-1',
    large: 'text-sm px-4 py-1.5'
  };

  const isClickable = status !== 'VERIFIED' && status !== 'SUBMITTED';

  return (
    <div 
      className="relative inline-block"
      onMouseEnter={() => setShowHover(true)}
      onMouseLeave={() => setShowHover(false)}
    >
      <button
        onClick={isClickable ? onClick : undefined}
        className={`
          inline-flex items-center gap-1.5 rounded-full border transition-all
          ${config.color}
          ${sizeClasses[size]}
          ${isClickable ? 'cursor-pointer hover:scale-105' : 'cursor-default'}
        `}
        data-testid="kyc-status-badge"
      >
        <Icon className={size === 'small' ? 'w-3 h-3' : 'w-3.5 h-3.5'} />
        <span>{config.label}</span>
      </button>

      {/* Tooltip */}
      {showTooltip && showHover && (
        <motion.div
          initial={{ opacity: 0, y: 5 }}
          animate={{ opacity: 1, y: 0 }}
          className="absolute left-1/2 -translate-x-1/2 top-full mt-2 z-50"
        >
          <div className="bg-black border border-white/20 rounded-lg px-3 py-2 text-xs text-white whitespace-nowrap shadow-xl">
            {config.tooltip}
            <div className="absolute left-1/2 -translate-x-1/2 -top-1 w-2 h-2 bg-black border-l border-t border-white/20 rotate-45" />
          </div>
        </motion.div>
      )}
    </div>
  );
}

// Export status check hook for other components
export function useKYCStatus(userId) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [canInvest, setCanInvest] = useState(false);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    if (userId) {
      fetchStatus();
    }
  }, [userId]);

  const fetchStatus = async () => {
    try {
      const response = await fetch(`${API_URL}/api/investor/kyc/status/${userId}`);
      const data = await response.json();
      setStatus(data.status || 'NOT_STARTED');
      setCanInvest(data.canInvest || false);
    } catch (error) {
      setStatus('NOT_STARTED');
      setCanInvest(false);
    } finally {
      setLoading(false);
    }
  };

  return { status, loading, canInvest, refetch: fetchStatus };
}
